import { gql } from '@apollo/client';

export const QuestionDefinitionFragment = gql`
  fragment QuestionDefinition on QuestionDefinition {
    ... on SelectQuestionDefinition {
      text
      choices
      selectedRange
    }
    ... on FreeTextQuestionDefinition {
      text
    }
    ... on PersonMultiselectQuestionDefinition {
      text
      organizationRespondents {
        id
        firstname
        surname
        contacts {
          type
          source
          value
        }
      }
      selectedRange
    }

    ... on SmsContactQuestionDefinition {
      text
      phoneNumber
    }

    ... on EmailContactQuestionDefinition {
      text
      email
    }

    ... on ScaleQuestionDefinition {
      text
      choices
      statements
      randomizeOrder
      isRankingScale
      showEscapeOption
      id
    }
  }
`;

export const ReportFragment = gql`
  fragment ReportFragment on PubReport {
    id
    addedOnFrom
    addedOnTo
    identifier
    totalRespondents
    surveyKeywordsGenerated
    anonymityEnabled
    anonymityForced
    anonymitySettings {
      default
      allowedLevels
    }
    firstSurvey {
      public
    }
    hiringAdditionalData {
      respondentId
      surveyId
      position
      isHired
      isRejected
      recruiterName
    }
    responses {
      id
      surveyId
      respondentId
      topicGroupId
      anonymous
      originalAnonymous
      finished
      additionallyAnonymized
      seqDate
      messages {
        id
        questionDefinitionIndex
        statementIndex
        comment
        text
        value
        anonymous
        originalAnonymous
        date
      }
      reactions {
        id
        channelId
        messageId
        respondentId
        text
        createdAt
      }
    }
    questions {
      id
      index
      text
      type
      selectedRange
      answersLocal
      statementsLocal
      showEscapeOption
      isRankingScale
      reportIndex
      answersToHighlight
    }
    respondents {
      id
      firstname
      surname
      addedOn
    }
    reportRoots
    metadata {
      surveyEnd
      surveyStart
      status
      accepted
      topicGroupId
      organization {
        id
        name
        featureNames
        primaryLanguageCode
        languages {
          code
        }
      }
      language {
        id
        code
      }
      respondent {
        id
        firstname
        surname
        intercomHash
        createdAt
        contacts {
          type
          value
          source
        }
        organizationRole
        organization {
          id
          name
          companyId
          featureNames
        }
        user {
          id
          username
          firstname
          surname
          intercomHash
          createdAt
          systemRole
          respondents {
            id
            firstname
            surname
            organizationRole
          }
        }
      }
      adminEmails
      organizationDepth
      productVersion {
        validFrom
        validTo
        product
      }
    }
    teams {
      id
      teamName
      directMembers
      memberTeams
      teamLeader
    }
    surveyGroup {
      from
      to
      topicGroup {
        id
        typeCode
        translations {
          id
          language {
            code
            id
          }
          value
          description
        }
      }
      steps {
        daysFromStart
        processStep {
          stepTopicGroup {
            id
            allowedAnonymities
            translations {
              id
              language {
                code
                id
              }
              value
              description
            }
          }
        }
      }
    }
    questionLabels {
      questionDefinitionIndex
      questionLabelComments {
        label
        commentIds
      }
    }
    source {
      respondentId
      sourceId
    }
    reportDefinition {
      id
      titleLocal
      aboutLocal
      sections {
        id
        heading
        headingLocal
        descriptionLocal
        type
        displayFilters {
          operator
          questionDefinitionIndex
          values
        }
        titleFilters {
          operator
          questionDefinitionIndex
          values
        }
        widgets {
          id
          questionDefinitionIndex
          headingLocal
          type
          descriptionLocal
          titleFilters {
            operator
            questionDefinitionIndex
            values
          }
          displayFilters {
            operator
            questionDefinitionIndex
            values
          }
          answersToHighlight
          values
        }
      }
      intro {
        questionDefinitionIndex
        headingLocal
        type
        descriptionLocal
        displayFilters {
          operator
          questionDefinitionIndex
          values
        }
        answersToHighlight
        values
        numerator
        denominator
        additionallyAnonymized
      }
    }
    additionalRespondentData {
      channelId
      position
      recruiter
      managers
      addedOn
    }
    sequenceFrom
    sequenceTo
  }
`;

export const ProcessReportRespondentDetailFragment = gql`
  fragment ProcessReportRespondentDetail on ProcessReportRespondentDetail {
    id
    identifier
    hiringAdditionalData {
      respondentId
      position
      isHired
      surveyId
      isRejected
      recruiterName
    }
    groupedRespondents {
      respondents {
        id
        preferredContactType
        firstname
        surname
        deleted
        contacts {
          type
          source
          value
          phoneNumber {
            countryCallingCode
            nationalNumber
          }
        }
      }
      addedOn
      surveys {
        id
        status
        sendAt
        endsAt
        public
        topicGroup {
          id
        }
        publicChannels {
          id
          respondentId
          lastRemindedAt
          maxAnonymousHighlightedLevel
          withAnswer
          finishedAt
          isFinishedByRespondent
        }
        respondentIds
      }
      hiringSurveys {
        id
        status
        sendAt
        endsAt
        topicGroup {
          id
        }
        publicChannels {
          id
          respondentId
          lastRemindedAt
          maxAnonymousHighlightedLevel
          withAnswer
          finishedAt
          isFinishedByRespondent
        }
        respondentIds
      }
      hiringAdditionalData {
        respondentId
        position
        isHired
        isRejected
        recruiterName
      }
      undeliveredInvitations {
        respondentId
        undeliveredSurveyInvitations {
          surveyId
          contact
          status
        }
      }
      metricQuestionsForRespondent {
        respondentId
        answers {
          topicGroupId
          value
        }
      }
      deletedRespondents
      hasUnreadMessages {
        respondentId
        unreadMessages
      }
    }
    questions {
      id
      index
      text
      type
      selectedRange
      answersLocal
      statementsLocal
      showEscapeOption
      isRankingScale
      reportIndex
      answersToHighlight
      metrics
      topicGroupId
    }
    responses {
      id
      surveyId
      respondentId
      topicGroupId
      anonymous
      originalAnonymous
      finished
      additionallyAnonymized
      seqDate
      reactions {
        id
        channelId
        messageId
        respondentId
        respondentFullName
        text
        createdAt
      }
      messages {
        id
        questionDefinitionIndex
        text
        value
        anonymous
        originalAnonymous
        statementIndex
        comment
        date
        hasAlert
      }
    }
    respondents {
      id
      firstname
      surname
      addedOn
    }
    reportRoots
    keyPoints {
      answerIds
      point
      sentiment
    }
    surveyGroup {
      id
      from
      to
      topicGroup {
        id
        typeCode
        hideOnboardingParts
        allowedAnonymities
        defaultAnonymity
        translations {
          id
          language {
            code
            id
          }
          value
          description
        }
      }
      steps {
        daysFromStart
        hasMetricQuestion
        status
        hasNotStarted
        notstartedReason
        surveys {
          id
          sendAt
          endsAt
          seqDate
          status
          public
        }
        processStep {
          id
          startOffsetMins
          reminders {
            reminderOffsetMins
          }
          endOffsetMins
          stepTopicGroup {
            allowedAnonymities
            defaultAnonymity
            id
            translations {
              id
              language {
                code
                id
              }
              value
              description
            }
          }
        }
      }
    }
    metadata {
      surveyEnd
      surveyStart
      status
      accepted
      organization {
        id
        name
        featureNames
        config {
          countryCallingCode
        }
      }
      language {
        id
        code
      }
      respondent {
        id
        firstname
        surname
        createdAt
        contacts {
          type
          value
          source
        }
        intercomHash
        organizationRole
        organization {
          id
          name
          companyId
          featureNames
        }
        user {
          id
          username
          firstname
          surname
          intercomHash
          createdAt
          systemRole
          respondents {
            id
            firstname
            surname
            organizationRole
          }
        }
      }
      adminEmails
      productVersion {
        validFrom
        validTo
        product
      }
    }
    teams {
      id
      teamName
      directMembers
      memberTeams
      teamLeader
    }
  }
`;

export const AiSurveySummaryFragment = gql`
  fragment AiSurveySummaryFragment on AiSurveySummaryData {
    text
    done
    hash
  }
`;

export const ProcessReportFragment = gql`
  fragment ProcessReportFragment on ProcessReportInfoSub {
    id
    identifier
    totalRespondents
    filteredRespondents
    addedOnFrom
    addedOnTo
    reportRoots
    hiringInfo {
      positions {
        id
        label
        candidateIds
      }
      recruiters {
        id
        label
        candidateIds
      }
      managers {
        id
        label
        candidateIds
      }
    }
    groupedRespondents {
      data {
        metricQuestionsForRespondent {
          respondentId
          answers {
            topicGroupId
            value
          }
        }
        respondents {
          id
          preferredContactType
          firstname
          surname
          deleted
          contacts {
            type
            source
            value
            phoneNumber {
              countryCallingCode
              nationalNumber
            }
          }
        }
        addedOn
        surveys {
          id
          status
          sendAt
          endsAt
          public
          topicGroup {
            id
          }
          respondentIds
          publicChannels {
            id
            respondentId
            lastRemindedAt
            maxAnonymousHighlightedLevel
            withAnswer
            isFinishedByRespondent
            finishedAt
          }
        }
        hiringSurveys {
          id
          status
          sendAt
          endsAt
          topicGroup {
            id
          }
          respondentIds
          publicChannels {
            id
            respondentId
            lastRemindedAt
            maxAnonymousHighlightedLevel
            withAnswer
            isFinishedByRespondent
            finishedAt
          }
        }
        hiringAdditionalData {
          respondentId
          position
          isHired
          isRejected
          recruiterName
        }
        deletedRespondents
        undeliveredInvitations {
          respondentId
          undeliveredSurveyInvitations {
            surveyId
            contact
            status
          }
        }
        hasUnreadMessages {
          respondentId
          unreadMessages
        }
      }
      totalCount
    }
    metadata {
      surveyEnd
      surveyStart
      status
      accepted
      organization {
        id
        name
        featureNames
        config {
          countryCallingCode
        }
      }
      language {
        id
        code
      }
      respondent {
        id
        firstname
        surname
        organizationRole
        createdAt
        intercomHash
        contacts {
          type
          value
          source
        }
        organization {
          id
          name
          companyId
          featureNames
        }
        user {
          id
          username
          firstname
          surname
          intercomHash
          createdAt
          systemRole
          respondents {
            id
            firstname
            surname
            organizationRole
          }
        }
      }
      adminEmails
      productVersion {
        validFrom
        validTo
        product
      }
    }
    widgets {
      withoutProblems {
        numerator
        denominator
      }
      responseRate {
        numerator
        denominator
      }
      anonymityRate {
        numerator
        denominator
      }
    }
    rootTeamsStatistics {
      teamId
      statistics {
        finishedRate {
          numerator
          denominator
        }
        responseRate {
          numerator
          denominator
        }
        anonymityRate {
          numerator
          denominator
        }
      }
    }
    teams {
      id
      teamName
      directMembers
      memberTeams
      teamLeader
    }

    surveyGroup {
      from
      to
      id
      statistics {
        total
        answered
        withProblems
      }
      topicGroup {
        id
        hideOnboardingParts
        typeCode
        allowedAnonymities
        defaultAnonymity
        translations {
          id
          language {
            code
            id
          }
          value
          description
        }
      }
      steps {
        daysFromStart
        hasMetricQuestion
        status
        hasNotStarted
        notstartedReason
        statistics {
          answered
          total
          withProblems
          anonymous
          avgMetric
        }
        surveys {
          id
          sendAt
          endsAt
          seqDate
          status
          public
        }
        processStep {
          id
          startOffsetMins
          reminders {
            reminderOffsetMins
          }
          endOffsetMins
          stepTopicGroup {
            allowedAnonymities
            defaultAnonymity
            id
            translations {
              id
              language {
                code
                id
              }
              value
              description
            }
          }
        }
      }
    }
  }
`;
